/**
 * MountedFolderTree
 *
 * Renders a mounted folder as a collapsible tree of subdirectories and .sql files.
 * Fetches directory contents via IPC on expand.
 */

import { useCallback, useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, FileCode2, Folder, FolderGit2, FolderOpen, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { AnimatedCollapse } from '../../workspace/start-page/components/AnimatedCollapse';
import { toast } from '../../../components/ui/toaster';
import { openScriptInTab } from '../services/scriptBrowserApi';
import { openDatabaseBlueprintTab } from '../../database-blueprint/services/openDatabaseBlueprintTab';
import { useMountedFolderIndexStore } from '../store/mountedFolderIndexStore';
import { MountedFolderContextMenu } from './MountedFolderContextMenu';
import { MountedFileContextMenu } from './MountedFileContextMenu';
import { listScriptBrowserDirectory } from '../services/scriptBrowserIpcApi';
import type { MountedFolder, DirectoryEntry } from '../types/scriptBrowserTypes';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type MountedFolderTreeProps = {
  folder: MountedFolder;
};

export function MountedFolderTree({ folder }: MountedFolderTreeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isScanning = useMountedFolderIndexStore((s) =>
    s.scanningFolderIds.includes(folder.id)
  );
  const indexedFiles = useMountedFolderIndexStore(
    (s) => s.index[folder.id]
  );
  const fileCount = indexedFiles?.length;

  const isBlueprint = folder.kind === 'blueprint' && folder.blueprintMetadata;
  const linkedDbs = folder.blueprintMetadata?.linkedDbs ?? [];
  const blueprintSubtitle = isBlueprint ? buildBlueprintSubtitle(linkedDbs) : '';

  return (
    <div
      className={`mb-1 ${isBlueprint ? 'rounded-sm bg-blue-50/40 dark:bg-blue-950/20' : ''}`}
      data-testid="mounted-folder-section"
      blueprint-data={isBlueprint ? 'true' : undefined}
      data-mounted-folder-id={folder.id}
    >
      <MountedFolderContextMenu folder={folder}>
        <Button
          variant="ghost"
          size="sm"
          className="w-full flex items-center justify-between h-auto py-1 px-2 text-xs font-medium"
          onClick={(e) => {
            if (isBlueprint && !e.altKey) {
              openDatabaseBlueprintTab(folder.id, folder.label);
              return;
            }
            setIsExpanded(!isExpanded);
          }}
          data-testid={isBlueprint ? 'blueprint-folder-open-tab' : undefined}
        >
          <div className="flex items-start gap-1.5 min-w-0 text-left">
            <div className="pt-0.5">
              {isBlueprint ? (
                <FolderGit2 className="h-3.5 w-3.5 shrink-0" />
              ) : isExpanded ? (
                <FolderOpen className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <Folder className="h-3.5 w-3.5 shrink-0" />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="truncate">{folder.label}</span>
                {isBlueprint && (
                  <span
                    className="inline-flex items-center rounded-sm bg-blue-200/70 dark:bg-blue-800/50 text-blue-800 dark:text-blue-200 px-1 py-px text-[10px] font-semibold uppercase tracking-wide"
                    data-testid="blueprint-folder-badge"
                  >
                    Blueprint
                  </span>
                )}
                <span className="text-muted-foreground shrink-0">
                  {isScanning ? (
                    <RandomDotmSpinner size={12} />
                  ) : !isBlueprint && fileCount !== undefined ? (
                    `(${fileCount})`
                  ) : null}
                </span>
              </div>
              {isBlueprint && (
                <span
                  className="truncate text-[10px] font-normal text-muted-foreground"
                  data-testid="blueprint-folder-subtitle"
                >
                  {blueprintSubtitle}
                </span>
              )}
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
        </Button>
      </MountedFolderContextMenu>
      <AnimatedCollapse isOpen={isExpanded}>
        {isBlueprint && (
          <div
            className="px-2 py-1 text-[10px] text-muted-foreground space-y-0.5"
            data-testid="blueprint-folder-link-rows"
          >
            {linkedDbs.map((l) => (
              <div key={l.label} className="truncate" data-testid={`blueprint-link-${l.label}`}>
                <span className="font-medium">{l.label}</span> · {l.connectionDisplayName} ·{' '}
                {l.database} · {l.lastSyncAt ? `last synced ${relativeTime(l.lastSyncAt)}` : 'not yet synced'}
              </div>
            ))}
          </div>
        )}
        <DirectoryContents
          directoryPath={folder.directoryPath}
          depth={0}
          folderId={folder.id}
        />
      </AnimatedCollapse>
    </div>
  );
}

function buildBlueprintSubtitle(
  linkedDbs: ReadonlyArray<{ lastSyncAt: string | null }>
): string {
  const n = linkedDbs.length;
  if (n === 0) return 'no linked databases';
  const synced = linkedDbs.filter((l) => l.lastSyncAt !== null).length;
  if (n === 1) return synced === 1 ? '1 linked DB · in sync' : '1 linked DB · not yet synced';
  if (synced === n) return `${n} linked DBs · in sync`;
  return `${n} linked DBs · ${synced} synced, ${n - synced} pending`;
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaSec = Math.floor((Date.now() - t) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}

/** Recursively renders directory contents (lazy-loaded on expand). */
function DirectoryContents({
  directoryPath,
  depth,
  folderId,
}: {
  directoryPath: string;
  depth: number;
  /** Root mounted-folder ID — used to subscribe to the tree-refresh nonce so
   *  the listing re-fetches after Refresh from DB writes new subfolders. */
  folderId: string;
}) {
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Re-fetch whenever the per-folder nonce bumps (e.g. after Refresh from DB).
  // Reads as a primitive so React only re-renders us when our folder's value
  // actually changes — not when an unrelated folder's nonce moves.
  const treeNonce = useMountedFolderIndexStore(
    (s) => s.treeNonce[folderId] ?? 0,
  );

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listScriptBrowserDirectory({ directoryPath });
      if (result.success) {
        setEntries(result.entries);
      } else {
        setError(result.error ?? 'Failed to read directory');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [directoryPath]);

  useEffect(() => {
    loadEntries();
    // `treeNonce` is intentionally a dep so a bump re-runs the fetch; the
    // linter can't see that because it's not referenced inside loadEntries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEntries, treeNonce]);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground"
        style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}>
        <RandomDotmSpinner size={12} />
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 text-xs text-destructive"
        style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}>
        <AlertTriangle className="h-3 w-3 shrink-0" />
        <span className="truncate">{error.includes('ENOENT') ? 'Folder not found' : error}</span>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="px-2 py-1 text-xs text-muted-foreground"
        style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}>
        No script files
      </div>
    );
  }

  return (
    <div className="py-0.5">
      {entries.map((entry) =>
        entry.isDirectory ? (
          <SubdirectoryItem
            key={entry.path}
            entry={entry}
            depth={depth + 1}
            folderId={folderId}
          />
        ) : (
          <FileItem key={entry.path} entry={entry} depth={depth + 1} />
        )
      )}
    </div>
  );
}

/** Expandable subdirectory within a mounted folder. */
function SubdirectoryItem({
  entry,
  depth,
  folderId,
}: {
  entry: DirectoryEntry;
  depth: number;
  folderId: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-left hover:bg-accent/50 rounded-sm cursor-pointer"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        {isExpanded ? (
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{entry.name}</span>
      </button>
      <AnimatedCollapse isOpen={isExpanded}>
        <DirectoryContents
          directoryPath={entry.path}
          depth={depth}
          folderId={folderId}
        />
      </AnimatedCollapse>
    </div>
  );
}

/** A script file (.sql or .ipynb) within a mounted folder tree. */
function FileItem({
  entry,
  depth,
}: {
  entry: DirectoryEntry;
  depth: number;
}) {
  const handleOpen = useCallback(async () => {
    const result = await openScriptInTab(entry.path);
    if (result === 'file-not-found') {
      toast.error({ title: 'File not found', description: entry.path });
    } else if (result === null) {
      toast.error({ title: 'Failed to open file', description: entry.path });
    }
  }, [entry.path]);

  const isNotebook = entry.name.toLowerCase().endsWith('.ipynb');
  const Icon = isNotebook ? BookOpen : FileCode2;

  return (
    <MountedFileContextMenu filePath={entry.path} onOpen={handleOpen}>
      <button
        type="button"
        className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-left hover:bg-accent/50 rounded-sm cursor-pointer"
        style={{ paddingLeft: `${depth * 16 + 20}px` }}
        onClick={handleOpen}
        title={entry.path}
      >
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate">{entry.name}</span>
      </button>
    </MountedFileContextMenu>
  );
}
